import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Editor from "react-simple-code-editor";
import TextAreaField from "../Form/TextAreaField";
import { HTTPFormProps } from "./HTTPForm";
import HTTPFormAddVariable from "./HTTPFormAddVariable";
import HTTPFormBodySelect from "./HTTPFormBodySelect";
import HTTPFormBodyStructured from "./HTTPFormBodyStructured";
import { highlightCode } from "./HTTPFormUtils";
import { HTTPFormMeta } from "./Meta";

export default function HTTPFormBody({
  formik,
  formPrefix,
  formValues,
  availableVariables,
}: HTTPFormProps) {
  const { t } = useTranslation();
  const editorRef = useRef<any>(null);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const [textareaEl, setTextareaEl] = useState<HTMLTextAreaElement | null>(
    null
  );

  useEffect(() => {
    if (editorContainerRef.current) {
      const textarea = editorContainerRef.current.querySelector("textarea");
      if (textarea) {
        setTextareaEl(textarea);
      }
    }
  }, [formValues.body_type]);

  const setContentType = (nextContentType: string) => {
    const currentBodyType = formValues.body_type;
    if (currentBodyType === nextContentType) {
      return;
    }

    const contentTypeIndex = formValues.headers
      ? formValues.headers.findIndex(
          (header) =>
            !header.key.localeCompare("Content-Type", undefined, {
              sensitivity: "accent",
            })
        )
      : -1;

    if (contentTypeIndex < 0 && nextContentType !== "NONE") {
      const updatedHeaders = [
        ...formValues.headers,
        {
          key: "Content-Type",
          value: nextContentType,
        },
      ];
      formik.setFieldValue(`${formPrefix}.headers`, updatedHeaders);
    } else {
      if (nextContentType !== "NONE") {
        formik.setFieldValue(`${formPrefix}.headers.${contentTypeIndex}`, {
          key: "Content-Type",
          value: nextContentType,
        });
      } else {
        const updatedHeaders = [...formValues.headers];
        updatedHeaders.splice(contentTypeIndex, 1);
        formik.setFieldValue(`${formPrefix}.headers`, updatedHeaders);
      }
    }

    const newBodyType = HTTPFormMeta.BODY_TYPES.find(
      (type) => type.value === nextContentType
    );
    if (!newBodyType) {
      return;
    }

    formik.setFieldValue(
      `${formPrefix}.${HTTPFormMeta.FIELDS.body_type.key}`,
      newBodyType.value
    );

    if (
      newBodyType.type === "STRUCTURED" &&
      (!formValues.body_params || formValues.body_params.length === 0)
    ) {
      formik.setFieldValue(`${formPrefix}.body_params`, [
        { key: "", value: "" },
      ]);
    }
  };

  const onChangeRequestBody = (body: string) => {
    formik.setFieldValue(`${formPrefix}.${HTTPFormMeta.FIELDS.body.key}`, body);
  };

  const addVariableToBody = (variable: string) => {
    if (
      typeof formValues.body_type === "string" &&
      formValues.body_type === "text/plain"
    ) {
      const currentBody = formValues.body || "";
      formik.setFieldValue(
        `${formPrefix}.${HTTPFormMeta.FIELDS.body.key}`,
        currentBody + variable
      );
      return;
    }

    try {
      if (textareaEl) {
        const startPos = textareaEl.selectionStart;
        const endPos = textareaEl.selectionEnd;
        const currentBody = formValues.body || "";

        const newBody =
          currentBody.substring(0, startPos) +
          variable +
          currentBody.substring(endPos);

        onChangeRequestBody(newBody);

        setTimeout(() => {
          if (textareaEl) {
            textareaEl.focus();
            const newPosition = startPos + variable.length;
            textareaEl.setSelectionRange(newPosition, newPosition);
          }
        }, 0);
      } else {
        const currentBody = formValues.body || "";
        onChangeRequestBody(currentBody + variable);
      }
    } catch (err) {
      const currentBody = formValues.body || "";
      onChangeRequestBody(currentBody + variable);
    }
  };

  return (
    <div className="mt-4">
      <HTTPFormBodySelect
        setContentType={setContentType}
        formPrefix={formPrefix}
      />
      {typeof formValues.body_type === "string" &&
        formValues.body_type === "text/plain" && (
          <div className="mt-2">
            <TextAreaField
              label={t(HTTPFormMeta.FIELDS.body.label)}
              name={`${formPrefix}.${HTTPFormMeta.FIELDS.body.key}`}
              help={t(HTTPFormMeta.FIELDS.body.help)}
              placeholder={t(HTTPFormMeta.FIELDS.body.placeholder)}
              rows={4}
            />

            <div className="mt-2 flex items-start justify-between">
              <div className="mr-4">
                <HTTPFormAddVariable
                  onAddVariable={addVariableToBody}
                  availableVariables={availableVariables}
                />
              </div>
            </div>
          </div>
        )}
      {typeof formValues.body_type === "string" &&
        (formValues.body_type === "application/json" ||
          formValues.body_type === "application/xml" ||
          formValues.body_type === "text/yaml") && (
          <div className="container" ref={editorContainerRef}>
            <Editor
              ref={editorRef}
              placeholder={t(
                "inbox-actions:builder.type-request-body",
                "Type request body"
              )}
              value={formValues.body || ""}
              onValueChange={onChangeRequestBody}
              className="my-4 bg-slate-50"
              highlight={(code) => highlightCode(code, formValues.body_type)}
              padding={10}
              style={{
                fontFamily: '"Fira code", "Fira Mono", monospace',
                fontSize: 12,
                minHeight: 300,
              }}
            />
            <div className="mt-2 flex items-start justify-between">
              <div className="mr-4">
                <HTTPFormAddVariable
                  onAddVariable={addVariableToBody}
                  availableVariables={availableVariables}
                />
              </div>
            </div>
          </div>
        )}
      {HTTPFormMeta.BODY_TYPES.some(
        (type) =>
          type.value === formValues.body_type && type.type === "STRUCTURED"
      ) && (
        <HTTPFormBodyStructured
          formik={formik}
          formPrefix={formPrefix}
          formValues={formValues}
          availableVariables={availableVariables}
        />
      )}
    </div>
  );
}
